import {
    ButtonGradientSecondaryToPrimary,
    ButtonSecondary,
    Colors,
    Icon,
    Text,
} from '@amelio/ui-library';
import { useEffect } from 'react';

import { isRefusedError } from '@/api/api';
import {
    useCompleteMeeting,
    useGetMeetingStatus,
    useUncompleteMeeting,
} from '@/api/review-services/post-review-meeting';
import { MeetingCompletionCard } from '@/components/MeetingCompletionCard/MeetingCompletionCard';
import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';
import { useAmelioTranslations } from '@/hooks';
import { showErrorToast } from '@/plugins/Toast';

interface MeetingCompletionActionProps {
    reviewInstanceId: string;
    hasSubmittedSurvey: boolean;
    employeeFirstName?: string;
    dataTestId?: string;
}

export const MeetingCompletionAction = ({
    reviewInstanceId,
    hasSubmittedSurvey,
    employeeFirstName,
    dataTestId = 'meeting-completion-action',
}: MeetingCompletionActionProps) => {
    const { t } = useAmelioTranslations();
    const planLock = usePlanLock();
    const firstName = employeeFirstName || t('postReviewMeeting.employeeFallback');

    const { data: meetingStatus } = useGetMeetingStatus(reviewInstanceId);
    const {
        mutate: completeMeeting,
        isPending: isCompleting,
        isError: hasCompleteMeetingError,
        error: completeMeetingError,
    } = useCompleteMeeting(reviewInstanceId);
    const { mutate: uncompleteMeeting, isPending: isUndoing } =
        useUncompleteMeeting(reviewInstanceId);

    const isCompleted = meetingStatus?.status === 'completed';
    const hasReviewInstance = Boolean(reviewInstanceId);

    useEffect(() => {
        if (!hasCompleteMeetingError || isRefusedError(completeMeetingError)) {
            return;
        }

        showErrorToast({ message: t('postReviewMeeting.completion.markCompletedError') });
    }, [hasCompleteMeetingError, completeMeetingError, t]);

    return (
        <>
            <MeetingCompletionCard
                icon={{ name: 'lucide-sparkles', color: Colors.WARNING }}
                title={t('postReviewMeeting.completion.confirmTitle')}
                subtitle={t('postReviewMeeting.completion.confirmSubtitle')}
                description={t('postReviewMeeting.completion.confirmDescription', { firstName })}
                className={'mb-6'}
                dataTestId={dataTestId}
            >
                {isCompleted ? (
                    <>
                        <span
                            className={
                                'flex items-center gap-2 rounded-full bg-teal-50 px-4 py-2 text-teal-700'
                            }
                            data-testid={'meeting-completed-badge'}
                        >
                            <Icon
                                name={'essential-tick-circle'}
                                size={'sm'}
                                color={Colors.PRIMARY_700}
                            />
                            <Text
                                size={'small'}
                                weight={'semibold'}
                                color={Colors.PRIMARY_700}
                            >
                                {t('postReviewMeeting.completion.completed')}
                            </Text>
                            {meetingStatus?.completedAt && (
                                <Text
                                    size={'small'}
                                    color={Colors.PRIMARY_700}
                                >
                                    {t('postReviewMeeting.completion.completedOn', {
                                        date: new Date(
                                            meetingStatus.completedAt,
                                        ).toLocaleDateString(),
                                    })}
                                </Text>
                            )}
                        </span>

                        <ButtonSecondary
                            onClick={() =>
                                planLock.isLocked ? planLock.onLocked() : uncompleteMeeting()
                            }
                            loading={!planLock.isLocked && isUndoing}
                            disabled={!planLock.isLocked && !hasReviewInstance}
                            leftIcon={planLock.isLocked ? <PlanLockIcon /> : undefined}
                            fitContent
                            dataTestId={'undo-completion-button'}
                        >
                            {t('postReviewMeeting.completion.undo')}
                        </ButtonSecondary>
                    </>
                ) : (
                    <>
                        <ButtonGradientSecondaryToPrimary
                            onClick={() =>
                                planLock.isLocked ? planLock.onLocked() : completeMeeting()
                            }
                            disabled={
                                !planLock.isLocked &&
                                (isCompleting || !hasReviewInstance || !hasSubmittedSurvey)
                            }
                            shape={'pill'}
                            fitContent
                            leftIcon={
                                planLock.isLocked ? (
                                    <PlanLockIcon color={Colors.WHITE} />
                                ) : (
                                    <Icon
                                        name={'essential-tick-circle'}
                                        color={Colors.WHITE}
                                        ariaHidden
                                    />
                                )
                            }
                            dataTestId={'mark-completed-button'}
                        >
                            {t('postReviewMeeting.completion.markCompleted')}
                        </ButtonGradientSecondaryToPrimary>

                        {!hasSubmittedSurvey && (
                            <div data-testid={'submission-required-hint'}>
                                <Text
                                    size={'small'}
                                    color={Colors.WARNING_800}
                                >
                                    {t('postReviewMeeting.completion.submissionRequiredHint')}
                                </Text>
                            </div>
                        )}
                    </>
                )}
            </MeetingCompletionCard>
            <div
                className={
                    '!mt-16 mb-8 flex items-center justify-center rounded-[15px] border border-slate-200 bg-white/45 p-7 backdrop-blur-[30px] md:!mt-40 md:p-6'
                }
            >
                <div
                    className={
                        'flex items-center gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4'
                    }
                    data-testid={'privacy-notice'}
                >
                    <div
                        className={
                            'flex items-center justify-center rounded-full bg-slate-100 p-1.5'
                        }
                    >
                        <Icon
                            name={'lucide-eye'}
                            size={'xl'}
                            color={Colors.SECONDARY_800}
                        />
                    </div>

                    <div>
                        <Text
                            weight={'semibold'}
                            color={Colors.BASE_600}
                            className={'!text-2xl'}
                        >
                            {t('postReviewMeeting.privacyNotice.title')}
                        </Text>

                        <Text
                            color={Colors.BASE_400}
                            className={'mt-2 !text-sm'}
                        >
                            {t('postReviewMeeting.privacyNotice.description')}
                        </Text>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MeetingCompletionAction;
